import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Loader2, Trash2, Mail, Users, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { apiClient } from "@/services/api";

interface ChildOrg {
  id: number;
  name: string;
  created_at: string;
  is_mssp: boolean;
  parent_organization_id: number | null;
}

interface OrgMember {
  id: number;
  name: string;
  email: string;
}

interface OrgInvite {
  id: number;
  email: string;
  status: string;
  created_at: string;
}

export function ManagedTenantsSection() {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newTenantName, setNewTenantName] = useState("");
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const [inviteOrgId, setInviteOrgId] = useState<number | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [expandedOrgId, setExpandedOrgId] = useState<number | null>(null);

  const { data: children = [], isLoading } = useQuery<ChildOrg[]>({
    queryKey: ["organization", "children"],
    queryFn: () => apiClient.get("/api/v1/organization/children"),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.post("/api/v1/organization/children", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", "children"] });
      queryClient.invalidateQueries({ queryKey: ["organizations", "accessible"] });
      setShowAddDialog(false);
      setNewTenantName("");
      toast.success("Tenant created");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to create tenant");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (orgId: number) =>
      apiClient.delete(`/api/v1/organization/children/${orgId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", "children"] });
      queryClient.invalidateQueries({ queryKey: ["organizations", "accessible"] });
      setConfirmRemoveId(null);
      toast.success("Tenant detached");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to remove tenant");
    },
  });

  const inviteMutation = useMutation({
    mutationFn: ({ orgId, email }: { orgId: number; email: string }) =>
      apiClient.post(`/api/v1/organization/${orgId}/invites`, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", "children", expandedOrgId, "invites"] });
      setInviteOrgId(null);
      setInviteEmail("");
      toast.success("Invite sent");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to send invite");
    },
  });

  const { data: expandedMembers = [] } = useQuery<OrgMember[]>({
    queryKey: ["organization", "children", expandedOrgId, "members"],
    queryFn: () => apiClient.get(`/api/v1/organization/${expandedOrgId}/members`),
    enabled: expandedOrgId !== null,
  });

  const { data: expandedInvites = [] } = useQuery<OrgInvite[]>({
    queryKey: ["organization", "children", expandedOrgId, "invites"],
    queryFn: () => apiClient.get(`/api/v1/organization/${expandedOrgId}/invites`),
    enabled: expandedOrgId !== null,
  });

  const handleCreate = () => {
    const name = newTenantName.trim();
    if (!name) return;
    createMutation.mutate(name);
  };

  const handleInvite = () => {
    const email = inviteEmail.trim();
    if (!email || !inviteOrgId) return;
    inviteMutation.mutate({ orgId: inviteOrgId, email });
  };

  const confirmingChild = children.find((c) => c.id === confirmRemoveId);
  const invitingChild = children.find((c) => c.id === inviteOrgId);

  return (
    <div className="space-y-6">
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">Managed Tenants</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Organizations managed under your MSSP account
            </p>
          </div>
          <Button
            size="sm"
            className="gap-1.5 rounded-full"
            onClick={() => setShowAddDialog(true)}
          >
            <Plus className="h-4 w-4" />
            Add Tenant
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : children.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-10 w-10 mb-3 text-muted-foreground opacity-40" />
              <p className="text-sm font-medium">No managed tenants</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add a tenant to start managing their security posture
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[32px]" />
                  <TableHead>Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[120px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {children.map((child) => {
                  const isExpanded = expandedOrgId === child.id;
                  return (
                    <>
                      <TableRow
                        key={child.id}
                        className="cursor-pointer"
                        onClick={() => setExpandedOrgId(isExpanded ? null : child.id)}
                      >
                        <TableCell className="w-[32px] pr-0">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{child.name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(child.created_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-primary"
                              onClick={() => setInviteOrgId(child.id)}
                              title="Invite user"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => setConfirmRemoveId(child.id)}
                              title="Remove tenant"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${child.id}-details`}>
                          <TableCell colSpan={4} className="bg-muted/30 px-8 py-4">
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <Users className="h-4 w-4" />
                                Members & Invites
                              </div>
                              {expandedMembers.length > 0 && (
                                <div className="space-y-1">
                                  {expandedMembers.map((m) => (
                                    <div key={m.id} className="flex items-center gap-2 text-sm">
                                      <span className="font-medium">{m.name}</span>
                                      <span className="text-muted-foreground">{m.email}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {expandedInvites.filter((i) => i.status === "pending").length > 0 && (
                                <div className="space-y-1">
                                  <p className="text-xs text-muted-foreground font-medium mt-2">Pending Invites</p>
                                  {expandedInvites
                                    .filter((i) => i.status === "pending")
                                    .map((i) => (
                                      <div key={i.id} className="flex items-center gap-2 text-sm">
                                        <span className="text-muted-foreground">{i.email}</span>
                                        <Badge variant="outline" className="text-xs">Pending</Badge>
                                      </div>
                                    ))}
                                </div>
                              )}
                              {expandedMembers.length === 0 && expandedInvites.filter((i) => i.status === "pending").length === 0 && (
                                <p className="text-sm text-muted-foreground">
                                  No members yet. Invite someone to get started.
                                </p>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5 rounded-full mt-2"
                                onClick={() => setInviteOrgId(child.id)}
                              >
                                <Mail className="h-3.5 w-3.5" />
                                Invite User
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Tenant Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="rounded-[15px]">
          <DialogHeader>
            <DialogTitle>Add Managed Tenant</DialogTitle>
            <DialogDescription>
              Create a new organization under your MSSP account.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Organization name"
              value={newTenantName}
              onChange={(e) => setNewTenantName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
              className="rounded-[10px]"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newTenantName.trim() || createMutation.isPending}
              className="rounded-full"
            >
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Dialog */}
      <Dialog
        open={confirmRemoveId !== null}
        onOpenChange={() => setConfirmRemoveId(null)}
      >
        <DialogContent className="rounded-[15px]">
          <DialogHeader>
            <DialogTitle>Remove Managed Tenant</DialogTitle>
            <DialogDescription>
              This will detach{" "}
              <strong>{confirmingChild?.name}</strong> from your MSSP
              account. Their data will not be deleted, but you will lose
              visibility into their security posture.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmRemoveId(null)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmRemoveId && removeMutation.mutate(confirmRemoveId)}
              disabled={removeMutation.isPending}
              className="rounded-full"
            >
              {removeMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Invite User Dialog */}
      <Dialog
        open={inviteOrgId !== null}
        onOpenChange={() => { setInviteOrgId(null); setInviteEmail(""); }}
      >
        <DialogContent className="rounded-[15px]">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation to join{" "}
              <strong>{invitingChild?.name}</strong>. They will receive
              an email with instructions to sign up.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInvite();
              }}
              className="rounded-[10px]"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setInviteOrgId(null); setInviteEmail(""); }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
              className="rounded-full"
            >
              {inviteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
